import React, { useMemo, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, HStack, IconButton, Input, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Portal, Select, Text, Tooltip, VStack, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiCopy, FiCreditCard, FiDownload, FiEdit2, FiEye, FiFileText, FiMinusCircle, FiMoreVertical, FiPaperclip, FiRotateCcw, FiSend,
} from 'react-icons/fi';
import { NIL } from 'uuid';
import { useQuery, QueryStatus } from 'react-query';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import IconInvoice from '../assets/Icon-Invoice.svg';
import {
  Invoice, InvoiceStatus, StripePaymentStatus, invoiceStatusLabel,
} from '../models/invoice';
import useInvoice from '../hooks/useInvoice';
import { useArchivedInvoices } from '../hooks/useInvoices';
import useCurrentUser from '../hooks/useCurrentUser';
import useGenerateInvoice from '../hooks/useGenerateInvoice';
import DocumentGridShell from './DocumentGridShell';
import LedgerTable, { LedgerColumn, LedgerBulkAction } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import { LEDGER_W } from './ledgerGrid';
import { isArchivedRow } from '../utils/archived'; // item 714
import StatusPill from './StatusPill';
import StripeIdChip from './StripeIdChip';
import usePortalShares from '../hooks/usePortalShares'; // item 997
import useWorkOrders from '../hooks/useWorkOrders'; // item 1475 — the invoice's WO leads
import PortalMark from './PortalMark'; // item 997 → 1449
import { useConfirm } from './ConfirmProvider';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';
import FinalizeInvoiceModal from './FinalizeInvoiceModal';
import WriteOffInvoiceModal from './WriteOffInvoiceModal'; // item 1564
import ApplyCreditInvoiceModal from './ApplyCreditInvoiceModal'; // item 1575
import WorkOrderInvoiceWizard, { WizardWorkOrder } from './WorkOrderInvoiceWizard'; // item 1583
import useCreditMemos from '../hooks/useCreditMemos';
import SendDocumentButton from './forms/SendDocumentButton';
import openPdf, { previewPdf } from '../utils/openPdf';
import { canArchiveInvoice } from '../utils/archiveRules';
import { money } from '../utils/money';
import { viewStoredFile } from '../hooks/useStoredFiles'; // item 1588 — payment report

// item 553 — Invoices on the shared LEDGER. Invoices are ONLY created by converting a Work Order
// (no ADD). Status is AUTOMATIC (the accountant advances New → Entered → Deposited on their
// dashboard) — the grid shows it read-only. The ONLY manual controls are the two ENTRY checkboxes
// (Do not enter · Write off) that PAUSE/EXCLUDE an invoice from QuickBooks entry. Void reopens the
// WO (backend), so void is terminal here.

// item 553/576 — the warm, status-aware invoice send copy comes from the server (editable in
// the dialog); the pay-now link is offered there.

// item 626 — invoiceStatusLabel now lives in models/invoice so every surface reads one label.
const pillLabel = (inv: Invoice): string => (inv.isFinalized ? invoiceStatusLabel(inv.status) : 'Draft');
const amountOf = (inv: Invoice): number => (inv as any).retailTotal ?? 0;
const isPaid = (inv: Invoice): boolean => inv.status === InvoiceStatus.Paid || inv.status === InvoiceStatus.PaidEntered;
// item 1578 — nothing left to collect (paid, deposited, Stripe-settled, or fully written off)
const isSettled = (inv: Invoice): boolean => isPaid(inv) || inv.status === InvoiceStatus.WrittenOff
  || !!inv.paidDate || !!inv.depositedDate
  || (inv.stripePaymentStatus ?? StripePaymentStatus.None) !== StripePaymentStatus.None;

// item 731 (Chad) — the automatic Stripe method label for a Stripe-paid invoice ('' when none),
// mirroring the item-579/586 deposits-ledger labels.
const stripeMethodLabel = (s?: StripePaymentStatus): string => {
  if (s === StripePaymentStatus.AchPaid) return 'Stripe ACH';
  if (s === StripePaymentStatus.CardPaid) return 'Stripe Credit Card';
  return '';
};

// item 562 — the grid twin of the PDF's diagonal FINALIZED watermark (item 441): a compact
// rubber-stamp mark under the status pill so a locked invoice reads as stamped at a glance.
const FinalizedStamp: React.FC = () => (
  <Box
    display="inline-block"
    transform="rotate(-6deg)"
    borderWidth="2px"
    borderColor="green.400"
    color="green.400"
    borderRadius="sm"
    px={1.5}
    lineHeight="1.4"
    fontSize="0.55rem"
    fontWeight="extrabold"
    letterSpacing="0.14em"
    opacity={0.85}
    userSelect="none"
  >
    FINALIZED
  </Box>
);

interface InvoicesGridProps {
  data?: Invoice[];
  status: QueryStatus;
  projectId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}

const InvoiceGrid: React.FC<InvoicesGridProps> = (props) => {
  const {
    data, status, projectId, collapse,
  } = props;
  const inProject = !!projectId && projectId !== NIL;
  const {
    tryArchive, tryVoid, voidMany, tryStatus, fetchById,
  } = useInvoice({ id: NIL });
  const { canModerate, isAdmin } = useCurrentUser(); // item 553 — write-off is admin (moderator) only
  // item 1575 — applying a credit to a finalized invoice is Admin + Accountant; offered only when
  // the customer has an open credit memo (or the invoice already carries one, to un-apply)
  const { memos: creditMemos } = useCreditMemos();
  const creditCustomers = useMemo(() => new Set(creditMemos.filter((m) => m.remaining > 0.004).map((m) => m.customerId)), [creditMemos]);
  const { capsFor } = usePortalShares();
  // item 1475 — leads live on the work order; the invoice row reads them through the cached WO index
  const { data: woIndex } = useWorkOrders();
  const leadLineFor = (workOrderId?: string | null): string | null => {
    if (!workOrderId) return null;
    const names = (woIndex?.index ?? []).find((w: any) => w.id === workOrderId)?.leadNames as string[] | undefined;
    return names && names.length > 0 ? `Lead: ${names.join(', ')}` : null;
  }; // item 997 — badge rows the customer portal can see
  const { data: archivedData } = useArchivedInvoices();
  const { generate } = useGenerateInvoice();
  const history = useHistory();
  const toast = useToast();
  // item 1500 (Chad) — the row ⋮ Void/Archive fired with NO confirm (the bulk bar asks; one
  // row didn't). Same shared dialog + wording family as the bulk bar. Void is permanent on an
  // invoice (there is no un-void — it reopens the work order and you regenerate from there).
  const confirm = useConfirm();
  // EPIC_VOID_FLOW_2026-10 — the shared void dialog asks (reason + Notify customer); no pre-confirm
  const confirmArchive = async (inv: Invoice) => {
    if (!(await confirm(
      `Archive ${inv.invoiceNumber || 'this invoice'}? It leaves the ledger (the Archived filter still shows it).`,
      { title: 'Archive invoice', confirmLabel: 'Archive', destructive: true },
    ))) return;
    tryArchive(inv.id);
  };
  // item 754 (Chad) — ONE-OFF: with the client's permission, debit their saved ACH bank for a
  // finalized unpaid invoice instead of sending a pay link. Rides the pay-link rails, so the
  // payment_intent.succeeded webhook marks it Stripe-paid + sends the receipt when it settles.
  // item 1349 (Chad) — the one-off charge now REQUIRES a typed authorization note (who
  // approved, when, how — e.g. "Approved by Ron via phone 9/1/2026"); it lands on the Stripe
  // PaymentIntent's description + metadata. No bank on file → the server's message points at
  // the pay-link path instead (methods on file ONLY — never key card details in; PCA/PCI).
  const [chargeInv, setChargeInv] = useState<Invoice | null>(null);
  const [chargeMemo, setChargeMemo] = useState('');
  const [charging, setCharging] = useState(false);
  // item 1349b (Chad: "does not show if they have an authorized bank or not") — look the
  // customer's saved ACH up the moment the modal opens; the charge stays disabled without one.
  const { data: bankOnFile, isLoading: bankLoading } = useQuery(
    ['bankOnFile', chargeInv?.customerId],
    async (): Promise<{ hasBank: boolean; bankName?: string | null; last4?: string | null }> => {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      const { data: res } = await axios.get(`${apiUrl}payment/bank-on-file/${chargeInv?.customerId}`);
      return res;
    },
    { enabled: !!chargeInv?.customerId },
  );
  const chargeBankOnFile = (inv: Invoice) => { setChargeInv(inv); setChargeMemo(''); };
  const doCharge = async () => {
    if (!chargeInv || !chargeMemo.trim()) return;
    setCharging(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}payment/charge-invoice/${chargeInv.id}`, { memo: chargeMemo.trim() });
      toast({ status: 'success', title: 'Charge started', description: 'ACH debit initiated — the invoice marks paid when it settles.' });
      setChargeInv(null);
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not charge', description: String(e?.response?.data ?? 'Try again.'), duration: 9000,
      });
    } finally {
      setCharging(false);
    }
  };

  const finalizeModal = useDisclosure();
  const [writeOffInv, setWriteOffInv] = useState<Invoice | null>(null); // item 1564
  const [creditInv, setCreditInv] = useState<Invoice | null>(null); // item 1575
  // item 1583 — "Rebill & write off" on a voided invoice: the WO's invoice wizard (no send), then
  // the write-off modal on the invoice it just finalized
  const [rebillWo, setRebillWo] = useState<WizardWorkOrder | null>(null);
  // item 1582 (Chad + Tom: write-offs got VOIDED by mistake, and void can't be undone) — an
  // unpaid finalized invoice's void dialog offers the write-off first.
  const confirmVoid = (inv: Invoice) => {
    const offerWriteOff = canModerate && inv.isFinalized && !isSettled(inv);
    tryVoid(inv.id, offerWriteOff ? {
      prompt: 'Work was done but you’re not billing it? Write it off — the invoice stays on the books.',
      label: 'Write off instead',
      run: () => setWriteOffInv(inv),
    } : undefined);
  };
  const [finalizeId, setFinalizeId] = useState('');
  // item 563 — the filter offers EVERY state an invoice can actually be in (the pill
  // vocabulary): Draft (unfinalized) + the finalized QB pipeline (New → Entered →
  // Deposited), Paid (paid at all, incl. deposited), the two ENTRY exceptions
  // (Do not enter · Written off), plus the Void and Archived buckets.
  const [statusFilter, setStatusFilter] = useState<'all' | 'drafts' | 'new' | 'entered'
  | 'paid' | 'deposited' | 'donotenter' | 'writtenoff' | 'void' | 'archived'>('all');
  const showingArchived = statusFilter === 'archived';
  // item 550/553 — "Send to client" opens the real branded email dialog (with a pay-now link)
  const [sendId, setSendId] = useState('');
  const [sendSignal, setSendSignal] = useState(0);

  const onPdfError = () => toast({ status: 'error', title: 'Could not open the PDF' });
  const previewInvoice = (id: string) => previewPdf(`invoice/${id}/pdf`).catch(onPdfError);
  const downloadInvoice = (id: string) => openPdf(`invoice/${id}/pdf`).catch(onPdfError);
  const accountingPdf = (id: string) => openPdf(`invoice/${id}/pdf?accounting=true`).catch(onPdfError);
  const tryEdit = (id: string) => history.push(`/invoice/${id}`);
  const openFinalize = (id: string) => { setFinalizeId(id); finalizeModal.onOpen(); };
  const sendToClient = (inv: Invoice) => {
    setSendId(inv.id);
    // item 576 — the milestone- AND status-aware copy now comes from the server (GET
    // email/copy → DocumentEmailCopy), the same rule every send entry point previews
    setSendSignal((s) => s + 1);
  };

  // item 553 — toggle an ENTRY exception status; unchecking returns the invoice to the New →
  // Entered → Deposited flow.
  const toggleStatus = (inv: Invoice, target: InvoiceStatus, on: boolean) => {
    tryStatus(inv.id, on ? target : InvoiceStatus.New);
  };

  const duplicateInvoice = async (inv: Invoice) => {
    if (!inv.workOrderId) {
      toast({ status: 'error', title: 'Can’t duplicate', description: 'This invoice has no work order to regenerate from.' });
      return;
    }
    try {
      const newId = await generate({ workOrderId: inv.workOrderId, itemize: !!inv.itemize }); // item 1488 — keep the source's itemize
      if (newId) history.push(`/invoice/${newId}`);
    } catch (e: any) {
      toast({ status: 'error', title: 'Action failed', description: e?.response?.data ?? 'The invoice was NOT created.' });
    }
  };

  const columns: LedgerColumn<Invoice>[] = useMemo(() => [
    {
      // item 1444 (Chad, after seeing the portal: "feels clean and gives us horiz space to get
      // full name") — INVOICE = the portal's pattern: number bold, job name as subtext beneath,
      // then customer · project (outside a project) and the SENT chip. The separate NAME column
      // is gone; this is the ledger's one flexible column with a 300px floor, so the whole job
      // name shows. Sort stays by number; search still covers the name.
      // item 1447 — the job name + customer · project moved OUT of this cell onto the full-width
      // rowSubline under the row (Chad: tired of it cutting off / wrapping). Number + SENT stay here.
      key: 'number',
      header: 'INVOICE',
      width: LEDGER_W.id,
      sortValue: (inv) => inv.invoiceNumber ?? '',
      render: (inv) => (
        <Box minW={0}>
          <HStack spacing={3} align="center">
            <Text fontWeight="semibold" whiteSpace="nowrap">{inv.invoiceNumber ?? '—'}</Text>
            {/* item 997 → item 1449 — portal visibility as a glyph beside the number: finalized invoices
                show in the customer's billing portal (amount + status + Pay by ACH) when their login has
                the grant. */}
            {inv.isFinalized && !inv.isVoid && capsFor((inv as any).customerId)?.invoices && (
              <PortalMark what="this invoice's amount and status, and can pay it by ACH" />
            )}
          </HStack>
          {/* item 777 (Chad) — SENT moved here from the crammed STATUS stack */}
          {inv.sentDate && (
          <Text fontSize="xs" color="blue.300" fontWeight="medium" whiteSpace="nowrap">
            {`SENT · ${SqlDateTimeToDisplay(inv.sentDate)}`}
          </Text>
          )}
        </Box>
      ),
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: LEDGER_W.created,
      sortValue: (inv) => (inv as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (inv as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (inv) => {
        const d = (inv as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (inv as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // item 817 (Chad) — an invoice's accountability is its FINALIZER (who locked it), not a
    // proposal approver. Column renamed FINALIZED BY; shows finalizedByName (dash until finalized).
    {
      key: 'finalizedBy',
      header: 'FINALIZED BY',
      width: LEDGER_W.slot3,
      sortValue: (inv) => (inv as any).finalizedByName ?? '',
      render: (inv) => {
        const name = (inv as any).finalizedByName as string | undefined;
        return name
          ? <Text fontSize="sm" noOfLines={1}>{name}</Text>
          : <Text fontSize="sm" opacity={0.4}>—</Text>;
      },
    },
    {
      key: 'amount',
      header: 'AMOUNT',
      width: LEDGER_W.slot4,
      align: 'right',
      sortValue: (inv) => amountOf(inv),
      // item 1578 (Chad: "the UX is fucking ugly") — a credit applied after finalize lowers what's
      // DUE, not what was billed: the column shows the invoice total, the credit sits under it.
      // item 1586 — a write-off reads the same way: billed total, "−$X written off" under it
      // (it used to sit in the ENTRY column).
      render: (inv) => {
        const credit = inv.creditAppliedAmount ?? 0;
        const writeOff = inv.writeOffAmount ?? 0;
        if (credit <= 0 && writeOff <= 0) return money(amountOf(inv));
        const due = amountOf(inv);
        return (
          <VStack align="end" spacing={0}>
            <Text>{money(due + credit + writeOff)}</Text>
            {credit > 0 && <Text fontSize="10px" color="purple.400" whiteSpace="nowrap">{`−${money(credit)} credit`}</Text>}
            {writeOff > 0 && <Text fontSize="10px" color="red.400" whiteSpace="nowrap">{`−${money(writeOff)} written off`}</Text>}
            {due > 0.004 && <Text fontSize="10px" opacity={0.7} whiteSpace="nowrap">{`${money(due)} due`}</Text>}
          </VStack>
        );
      },
    },
    {
      key: 'status',
      header: 'STATUS',
      width: LEDGER_W.status,
      sortValue: (inv) => inv.status,
      // item 562 — finalized invoices carry the rubber-stamp mark under the pill
      // item 731 (Chad) — a Stripe-paid invoice shows the ACH/card method + its copyable
      // payment-intent id right in the ledger, so it's visible working the project.
      // item 777 (Chad) — STATUS was stacking four things (pill / stamp / SENT / Stripe badge)
      // and eating vertical space. Now: pill + stamp only; SENT lives under the NAME; the
      // Stripe payment has its own PAYMENT column.
      render: (inv) => (
        <VStack align="start" spacing={1}>
          <StatusPill label={pillLabel(inv)} isVoid={inv.isVoid} minW="112px" />
          {inv.isFinalized && !inv.isVoid && <FinalizedStamp />}
        </VStack>
      ),
    },
    {
      key: 'payment',
      header: 'PAYMENT',
      width: LEDGER_W.extra,
      sortValue: (inv) => stripeMethodLabel(inv.stripePaymentStatus),
      render: (inv) => {
        if (stripeMethodLabel(inv.stripePaymentStatus)) {
          return (
            // item 737/777 — ONE subtle "paid" badge + icon-only copy (full pi_… id in the tooltip)
            <HStack spacing={1} align="center">
              <Badge colorScheme="green" variant="subtle" fontSize="0.6rem" borderRadius="sm" textTransform="none">
                {`${stripeMethodLabel(inv.stripePaymentStatus)} · paid`}
              </Badge>
              <StripeIdChip id={inv.stripePaymentReference} label="Payment id" iconOnly />
            </HStack>
          );
        }
        // item 1265 (Chad: "payment is empty" on a DEPOSITED invoice) — the column only knew
        // Stripe; a manually-collected payment (check/wire/legacy) showed a bare dash. Fall
        // back to the manual facts: deposit method/reference, else the paid date.
        const manualPaid = inv.status === InvoiceStatus.Paid || inv.status === InvoiceStatus.PaidEntered
          || !!inv.paidDate || !!inv.depositedDate;
        if (!manualPaid) return <Text fontSize="xs" opacity={0.4}>—</Text>;
        const reference = (inv.depositReference ?? '').trim();
        // item 1578 — settled by a credit memo: name the memo ("CM-7 · paid …") so it fits like "Wire"
        const rawMethod = (inv.depositMethod ?? '').trim();
        const method = rawMethod === 'Credit memo' && reference ? reference : rawMethod;
        const when = inv.paidDate ?? inv.depositedDate;
        const pill = (
          <Badge colorScheme="green" variant="subtle" fontSize="0.6rem" borderRadius="sm" textTransform="none" title={reference || undefined}>
            {`${method || 'manual'} · paid${when ? ` ${SqlDateTimeToDisplay(when)}` : ''}`}
          </Badge>
        );
        // item 1588/1589 — the payment report / check image recorded with the payment
        if (!inv.depositCheckFileId) return pill;
        return (
          <HStack spacing={1} align="center">
            {pill}
            <Tooltip label="Open the payment report / check image" openDelay={300}>
              <IconButton
                aria-label="Open the payment report / check image"
                icon={<FiPaperclip />}
                size="xs"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); viewStoredFile(inv.depositCheckFileId!); }}
              />
            </Tooltip>
          </HStack>
        );
      },
    },
    {
      // item 553 — the ONLY manual status controls: two checkboxes that pause QB entry.
      // Status otherwise advances automatically (accountant dashboard).
      key: 'entry',
      header: 'ENTRY',
      width: LEDGER_W.extra,
      render: (inv) => {
        if (!inv.isFinalized || inv.isVoid) return null;
        return (
          <VStack align="start" spacing={0} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              size="sm"
              isChecked={inv.status === InvoiceStatus.DoNotEnter}
              onChange={(e) => toggleStatus(inv, InvoiceStatus.DoNotEnter, e.target.checked)}
            >
              <Text fontSize="xs">Do not enter</Text>
            </Checkbox>
            {/* item 1586 — Write off moved to the row ⋮ menu; the amount shows under AMOUNT */}
            {/* item 1575 — the customer took their credit memo off a payment */}
            {isAdmin && ((inv.creditAppliedAmount ?? 0) > 0 || creditCustomers.has(inv.customerId)) && (
              <Button size="xs" variant="link" colorScheme="purple" onClick={() => setCreditInv(inv)}>
                {(inv.creditAppliedAmount ?? 0) > 0 ? 'Credits…' : 'Apply credit…'}
              </Button>
            )}
          </VStack>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [inProject, canModerate, isAdmin, creditCustomers, capsFor]); // item 997 — capsFor drives the SHARED TO PORTAL chip

  // shared trailing icons, aligned on every row
  const iconSpacer = <Box w="24px" flexShrink={0} />;
  const previewBtn = (inv: Invoice) => (
    <Tooltip label="Preview" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => previewInvoice(inv.id)} />
    </Tooltip>
  );
  const downloadBtn = (inv: Invoice) => (
    <Tooltip label="Download PDF" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => downloadInvoice(inv.id)} />
    </Tooltip>
  );

  const rowActions = (inv: Invoice): React.ReactNode => {
    // item 553 — VOID invoices stay (viewable); void is TERMINAL (it reopened the WO) — no Restore.
    if (inv.isVoid || showingArchived) {
      // item 1583 — a voided invoice whose work order has nothing live on it can be rebilled
      // straight into a write-off (the paper trail Chad + Tom rebuilt by hand)
      const wo = inv.workOrderId ? (woIndex?.index ?? []).find((w: any) => w.id === inv.workOrderId) : undefined;
      const rebill: WizardWorkOrder | null = canModerate && inv.isVoid && !showingArchived && wo && !wo.isVoid && !wo.hasLiveInvoice
        ? {
          id: wo.id,
          workOrderNumber: wo.workOrderNumber,
          customerName: wo.customerName,
          retailTotal: wo.retailTotal,
          isSalesOrder: wo.isSalesOrder,
          proposalId: wo.proposalId ?? null,
        }
        : null;
      return (
        <HStack spacing={1} justify="flex-end">
          {iconSpacer}
          {previewBtn(inv)}
          {downloadBtn(inv)}
          {rebill ? (
            <Menu isLazy>
              <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
              <Portal>
                <MenuList fontSize="sm">
                  <MenuItem
                    icon={<FiRotateCcw />}
                    onClick={() => setRebillWo(rebill)}
                  >
                    Rebill &amp; write off…
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          ) : iconSpacer}
        </HStack>
      );
    }
    const isDraft = !inv.isFinalized && !isArchivedRow(inv);
    return (
      <HStack spacing={1} justify="flex-end">
        {isDraft && <Button size="xs" minW="76px" colorScheme="brandGreen" onClick={() => openFinalize(inv.id)}>Finalize</Button>}
        <Tooltip label="Duplicate" openDelay={300}>
          <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FiCopy />} onClick={() => duplicateInvoice(inv)} />
        </Tooltip>
        {previewBtn(inv)}
        {downloadBtn(inv)}
        <Menu isLazy>
          <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
          <Portal>
            <MenuList fontSize="sm">
              <MenuItem icon={<FiEdit2 />} onClick={() => tryEdit(inv.id)}>Edit</MenuItem>
              {/* item 553 — send the finalized invoice to the client (branded email + pay link) */}
              {inv.isFinalized && <MenuItem icon={<FiSend />} onClick={() => sendToClient(inv)}>Send to client</MenuItem>}
              {/* item 754 (Chad) — one-off saved-bank charge, with permission (admins only) */}
              {canModerate && inv.isFinalized && !inv.isVoid
                && inv.stripePaymentStatus === StripePaymentStatus.None && (
                <MenuItem icon={<FiCreditCard />} onClick={() => chargeBankOnFile(inv)}>Charge bank on file…</MenuItem>
              )}
              <MenuItem icon={<FiFileText />} onClick={() => accountingPdf(inv.id)}>Accounting PDF</MenuItem>
              {canModerate && <MenuDivider />}
              {/* item 1564/1578/1586 — write off part or all of the open balance (or see/undo
                  past ones); lives here now, not in the ENTRY column */}
              {canModerate && inv.isFinalized && (!isSettled(inv) || (inv.writeOffAmount ?? 0) > 0) && (
                <MenuItem icon={<FiMinusCircle />} onClick={() => setWriteOffInv(inv)}>
                  {(inv.writeOffAmount ?? 0) > 0 ? 'Write-offs…' : 'Write off…'}
                </MenuItem>
              )}
              {/* item 1500 — confirm first, exactly like the bulk bar */}
              {canModerate && <MenuItem onClick={() => confirmVoid(inv)}>Void</MenuItem>}
              {canModerate && canArchiveInvoice(inv) && <MenuItem onClick={() => confirmArchive(inv)}>Archive</MenuItem>}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  // checkboxes → BULK housekeeping only (archive / void)
  const bulkActions: LedgerBulkAction<Invoice>[] = canModerate ? [
    {
      label: 'Archive',
      appliesTo: (inv) => !isArchivedRow(inv) && canArchiveInvoice(inv), // item 1570 — void first
      confirm: (rows) => `Archive ${rows.length} invoice(s)?`,
      onRun: async (rows) => { await Promise.all(rows.map((inv) => tryArchive(inv.id))); },
    },
    {
      label: 'Void',
      colorScheme: 'red',
      appliesTo: (inv) => !inv.isVoid,
      onRun: async (rows) => { await voidMany(rows.map((inv) => inv.id)); }, // EPIC_VOID_FLOW — one dialog (reason + notify)
    },
  ] : [];

  const rows = useMemo(() => {
    if (statusFilter === 'archived') {
      return ((archivedData?.index as Invoice[]) ?? []).filter((inv) => !inProject || inv.projectId === projectId);
    }
    const list = (data ?? []).filter((inv) => !isArchivedRow(inv));
    const live = (inv: Invoice) => !inv.isVoid;
    switch (statusFilter) {
      case 'drafts': return list.filter((inv) => !inv.isFinalized && live(inv));
      case 'new': return list.filter((inv) => inv.isFinalized && inv.status === InvoiceStatus.New && live(inv));
      case 'entered': return list.filter((inv) => inv.status === InvoiceStatus.NewEntered && live(inv));
      case 'paid': return list.filter((inv) => isPaid(inv) && live(inv));
      case 'deposited': return list.filter((inv) => inv.status === InvoiceStatus.PaidEntered && live(inv));
      case 'donotenter': return list.filter((inv) => inv.status === InvoiceStatus.DoNotEnter && live(inv));
      case 'writtenoff': return list.filter((inv) => inv.status === InvoiceStatus.WrittenOff && live(inv));
      case 'void': return list.filter((inv) => inv.isVoid);
      default: return list;
    }
  }, [data, statusFilter, archivedData, inProject, projectId]);

  return (
    <>
      <DocumentGridShell
        heading="INVOICES"
        icon={IconInvoice}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        // item 712 (Chad) — header count must match the visible (archived-filtered) rows
        data={data === undefined ? undefined : rows}
      >
        <LedgerTable<Invoice>
          defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
          rows={rows}
          columns={columns}
          rowSubline={(inv) => (
            <LedgerSubline
              name={inv.serviceName || inv.customerName}
              meta={[
                ...(inProject ? [] : [inv.customerName, inv.projectName]),
                // item 1475 — an invoice carries no lead of its own: read its work order's (already cached)
                leadLineFor(inv.workOrderId),
              ]}
            />
          )}
          rowId={(inv) => inv.id}
          rowActions={rowActions}
          rowActionsWidth={LEDGER_W.actions}
          rowActionsFlex // item 1449 — same actions width on every ledger so the columns align down a project page
          rowActionsHeader="ACTION"
          bulkActions={showingArchived ? [] : bulkActions}
          selectable={canModerate && !showingArchived}
          filters={(
            <Select size="sm" maxW="200px" variant="filled" borderRadius="lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="all">ALL STATUSES</option>
              <option value="drafts">DRAFTS</option>
              <option value="new">NEW</option>
              <option value="entered">ENTERED</option>
              <option value="paid">PAID</option>
              <option value="deposited">DEPOSITED</option>
              <option value="donotenter">DO NOT ENTER</option>
              <option value="writtenoff">WRITTEN OFF</option>
              <option value="void">VOID</option>
              <option value="archived">ARCHIVED</option>
            </Select>
          )}
          searchText={(inv) => `${inv.invoiceNumber ?? ''} ${inv.serviceName ?? ''} ${inv.customerName ?? ''}`}
          searchPlaceholder="Search invoices…"
          onRowClick={showingArchived ? undefined : (inv) => tryEdit(inv.id)}
          emptyText={showingArchived ? 'No archived invoices.' : 'No invoices yet — generate one from a work order.'}
        />
      </DocumentGridShell>
      <WriteOffInvoiceModal
        isOpen={!!writeOffInv}
        onClose={() => setWriteOffInv(null)}
        invoiceId={writeOffInv?.id ?? null}
        invoiceNumber={writeOffInv?.invoiceNumber ?? 'Invoice'}
        openBalance={writeOffInv && writeOffInv.status !== InvoiceStatus.WrittenOff ? (writeOffInv.retailTotal ?? 0) : 0}
        isPaid={!!writeOffInv && (isPaid(writeOffInv) || !!writeOffInv.paidDate || !!writeOffInv.depositedDate
          || (writeOffInv.stripePaymentStatus ?? StripePaymentStatus.None) !== StripePaymentStatus.None)}
      />
      {/* item 1583 — rebill a voided invoice's work order, then write the new invoice off */}
      <WorkOrderInvoiceWizard
        isOpen={!!rebillWo}
        onClose={() => setRebillWo(null)}
        workOrder={rebillWo ?? undefined}
        writeOff
        onFinalizedInvoice={async (id) => {
          setRebillWo(null);
          const fresh = await fetchById(id).catch(() => undefined);
          if (fresh) setWriteOffInv(fresh as Invoice);
        }}
      />
      <ApplyCreditInvoiceModal
        isOpen={!!creditInv}
        onClose={() => setCreditInv(null)}
        invoiceId={creditInv?.id ?? null}
        invoiceNumber={creditInv?.invoiceNumber ?? 'Invoice'}
        customerId={creditInv?.customerId ?? null}
        balanceDue={creditInv && creditInv.status !== InvoiceStatus.WrittenOff ? (creditInv.retailTotal ?? 0) : 0}
        isPaid={!!creditInv && (isPaid(creditInv) || !!creditInv.paidDate || !!creditInv.depositedDate
          || (creditInv.stripePaymentStatus ?? StripePaymentStatus.None) !== StripePaymentStatus.None)}
      />
      <FinalizeInvoiceModal
        isOpen={finalizeModal.isOpen}
        onClose={finalizeModal.onClose}
        invoiceId={finalizeId}
        // item 771 (Chad) — 733D's 'Finalize & send' was DEAD CODE: no host ever passed showSend,
        // so finalizing from the ledger never offered the send. Now it does — choosing it opens
        // the branded send dialog for the just-finalized invoice.
        showSend
        onFinalized={(send) => {
          if (send && finalizeId) {
            setSendId(finalizeId);
            setSendSignal((sig) => sig + 1);
          }
        }}
      />
      {/* item 553 — the real-email "Send to client" dialog (branded, with the Stripe pay link);
          item 576 — subject/body pre-fill from the server's status-aware copy rule */}
      <SendDocumentButton
        hideTrigger
        documentType="Invoice"
        documentId={sendId}
        autoOpenSignal={sendSignal}
      />
      {/* item 1349 — the memo-required charge dialog (bank on file only) */}
      <Modal isOpen={!!chargeInv} onClose={() => setChargeInv(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Charge bank on file</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" mb={3}>
              {chargeInv ? `This debits the client's saved bank for ${money(amountOf(chargeInv))}, one time. ` : ''}
              The invoice marks paid when the ACH debit settles. No card or bank details are ever
              typed in — saved methods only; use the pay link if nothing is on file.
            </Text>
            {/* item 1349b — the saved bank, or the lack of one, as a clear card */}
            {bankLoading && <Text fontSize="sm" opacity={0.6} mb={3}>Checking for a saved bank…</Text>}
            {!bankLoading && bankOnFile?.hasBank && (
              <HStack mb={3} p={3} borderWidth="1px" borderRadius="md" borderColor="green.400" justify="space-between">
                <Text fontSize="sm" fontWeight="semibold">
                  {`${bankOnFile.bankName ?? 'Bank account'} ••${bankOnFile.last4 ?? ''}`}
                </Text>
                <Badge colorScheme="green" variant="subtle">BANK ON FILE</Badge>
              </HStack>
            )}
            {!bankLoading && bankOnFile && !bankOnFile.hasBank && (
              <HStack mb={3} p={3} borderWidth="1px" borderRadius="md" borderColor="orange.400" justify="space-between">
                <Text fontSize="sm" fontWeight="semibold">No bank on file for this client</Text>
                <Badge colorScheme="orange" variant="subtle">SEND THE PAY LINK</Badge>
              </HStack>
            )}
            <Input
              placeholder="Authorization note (required) — e.g. Approved by Ron via phone 9/1/2026"
              value={chargeMemo}
              onChange={(e) => setChargeMemo(e.target.value)}
              isDisabled={!bankOnFile?.hasBank}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setChargeInv(null)}>Cancel</Button>
            <Button colorScheme="red" isDisabled={!chargeMemo.trim() || !bankOnFile?.hasBank} isLoading={charging} onClick={doCharge}>
              Client approved — charge now
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

InvoiceGrid.defaultProps = {
  data: undefined,
  projectId: NIL,
  collapse: undefined,
};

export default InvoiceGrid;
